import { useMemo } from 'react';

import { Badge } from '@/components/ui/badge.js';
import type { QueueWait } from '@/lib/api/quality-types.js';

import { formatElapsed, useTickingNow } from './elapsed.js';

const STATE_TEXT: Record<QueueWait['state'], string> = {
  waiting_for_worker: 'In queue',
  waiting_for_browserstack: 'Waiting for a BrowserStack session',
  waiting_for_device: 'Waiting for a device',
  waiting_for_project_slot: 'Waiting for a free slot in this project',
  queue_paused: 'Queue paused by an administrator',
  no_workers: 'No worker is connected',
};

function ordinal(position: number): string {
  const remainderOfHundred = position % 100;
  if (remainderOfHundred >= 11 && remainderOfHundred <= 13)
    return `${position}th`;
  const suffix = ['th', 'st', 'nd', 'rd'][position % 10] ?? 'th';
  return `${position}${suffix}`;
}

const REASON_TEXT: Record<
  NonNullable<QueueWait['no_estimate_reason']>,
  string | null
> = {
  not_enough_history: 'no estimate yet, not enough history',
  human_input: 'a run ahead is waiting on a person',
  queue_paused: null,
  no_workers: null,
};

/** "expected to start in 10–25 min · low confidence", or the reason there is no window. */
function estimateText(queueWait: QueueWait): string | null {
  if (queueWait.estimated_wait_seconds) {
    const { low, high } = queueWait.estimated_wait_seconds;
    const window = `expected to start in ${formatWindow(low, high)}`;
    return queueWait.confidence
      ? `${window} · ${queueWait.confidence} confidence`
      : window;
  }
  return queueWait.no_estimate_reason
    ? REASON_TEXT[queueWait.no_estimate_reason]
    : null;
}

/**
 * "under a minute", "3 min", "10–25 min", "2–3 h", or "10 min–2 h" when the bounds fall in
 * different units. Each bound keeps its own unit, so a mixed window never inflates its low end.
 */
export function formatWindow(lowSeconds: number, highSeconds: number): string {
  const low = boundOf(lowSeconds);
  const high = boundOf(highSeconds);
  if (high.value < 1) return 'under a minute';
  if (low.value < 1) return `up to ${high.value} ${high.unit}`;
  if (low.unit === high.unit) {
    return low.value === high.value
      ? `${high.value} ${high.unit}`
      : `${low.value}–${high.value} ${high.unit}`;
  }
  return `${low.value} ${low.unit}–${high.value} ${high.unit}`;
}

function boundOf(seconds: number): { value: number; unit: 'min' | 'h' } {
  const minutes = Math.round(seconds / 60);
  return minutes >= 120
    ? { value: Math.round(minutes / 60), unit: 'h' }
    : { value: minutes, unit: 'min' };
}

/** "2nd in line" when the gate knows the order, "4 jobs in queue" when it only knows the depth. */
function lineText(queueWait: QueueWait): string | null {
  if (queueWait.queue_position != null)
    return `${ordinal(queueWait.queue_position)} in line`;
  if (
    queueWait.queue_length != null &&
    queueWait.state === 'waiting_for_worker'
  )
    return `${queueWait.queue_length} job${queueWait.queue_length === 1 ? '' : 's'} in queue`;
  return null;
}

/**
 * Milliseconds the run has waited, ticking once a second between refreshes. Each refetch hands in
 * a new `queue_wait` object, which re-anchors the clock on the server's `waited_seconds`, so the
 * counter never drifts further than one refresh interval from the truth. The last tick can predate
 * that refetch, so the server's reading is the floor: a refresh never steps the counter back.
 */
function useWaitedMs(queueWait: QueueWait | null | undefined): number {
  const startedWaitingAt = useMemo(
    () => (queueWait ? Date.now() - queueWait.waited_seconds * 1_000 : null),
    [queueWait],
  );
  const now = useTickingNow(startedWaitingAt != null);
  if (startedWaitingAt == null) return 0;
  return Math.max(
    (queueWait?.waited_seconds ?? 0) * 1_000,
    now - startedWaitingAt,
  );
}

/** The run-detail line: gate, elapsed wait, and the run's place in the line when known. */
export function QueueWaitNotice({
  queueWait,
}: {
  queueWait: QueueWait | null | undefined;
}) {
  const waitedMs = useWaitedMs(queueWait);
  if (!queueWait) return null;
  const parts = [
    STATE_TEXT[queueWait.state] ?? STATE_TEXT.waiting_for_worker,
    `waiting for ${formatElapsed(waitedMs)}`,
    lineText(queueWait),
    estimateText(queueWait),
  ];
  return (
    <p
      data-testid="queue-wait-notice"
      className="text-sm text-muted-foreground"
    >
      {parts.filter(Boolean).join(' · ')}
    </p>
  );
}

/** The runs-list hint beside the status badge: just "In queue" and the elapsed wait. */
export function QueueWaitHint({
  queueWait,
}: {
  queueWait: QueueWait | null | undefined;
}) {
  const waitedMs = useWaitedMs(queueWait);
  if (!queueWait) return null;
  const window = queueWait.estimated_wait_seconds
    ? ` · ~${formatWindow(queueWait.estimated_wait_seconds.low, queueWait.estimated_wait_seconds.high)}`
    : '';
  return (
    <Badge variant="outline" data-testid="queue-wait-hint">
      {`In queue · ${formatElapsed(waitedMs)}${window}`}
    </Badge>
  );
}
